import { useMemo } from 'react'
import { useOrionDomainQuery } from '../../../../api/coreData'
import {
  DYNAMIC_CATEGORY_EXCLUSIONS,
  DYNAMIC_VALUE_EXCLUSIONS,
  GROUPING_OPTIONS,
  PRIMARY_GROUPING_CATEGORIES,
  SECONDARY_GROUPING_CATEGORIES
} from '../lib/constants'

// Orion serializes each option with camelCase fields:
//   { grouping: 'Security Classification 1', value: <int>, name: '<firm label>',
//     category: '<firm-assigned category>', isAssetBased, additionalOptions, ... }
// The `grouping` field is the StringValue of the CalculationGrouping enum and
// is what we pass back to Orion on $type:'grouping' nodes.
const SC_PREFIX = 'Security Classification '

// Fallback category for SC entries that come back without one (defensive --
// the OAT response always populates this for SC slots, but skip-if-empty).
const SC_FALLBACK_CATEGORY = 'Asset'

const KNOWN_CATEGORIES = new Set([
  ...PRIMARY_GROUPING_CATEGORIES,
  ...SECONDARY_GROUPING_CATEGORIES
])

/**
 * Index Envision response entries by `grouping` wire value, filtered to
 * asset-based, non-excluded rows.
 *
 * @param {Array<object>} envisionResponse
 * @returns {Map<string, object>}
 */
function indexLive (envisionResponse) {
  const map = new Map()
  for (const o of envisionResponse) {
    if (!o || typeof o.grouping !== 'string') continue
    if (!o.isAssetBased) continue
    if (DYNAMIC_VALUE_EXCLUSIONS.has(o.grouping)) continue
    // Category exclusions apply to non-SC entries only. SC categories are
    // firm-controlled; we surface SC slots regardless of how the firm
    // chose to categorize them (a firm that puts SC1 under "Billing"
    // should still see it in the picker).
    const isSC = o.grouping.startsWith(SC_PREFIX)
    if (!isSC && DYNAMIC_CATEGORY_EXCLUSIONS.has(o.category)) continue
    map.set(o.grouping, o)
  }
  return map
}

/**
 * Merge curated statics with the live (per-client) Envision catalog.
 *
 * Statics retain their hardcoded category and label -- the endpoint
 * contributes `additionalOptions` and `isAssetBased` only. SC entries and
 * other dynamic extras inherit category/label from the endpoint.
 *
 * Each entry is tagged with `source`:
 *   - 'static'           : in GROUPING_OPTIONS, no live data yet
 *   - 'static-augmented' : in GROUPING_OPTIONS, augmented with live metadata
 *   - 'dynamic-sc'       : not a static; wire value starts with 'Security Classification '
 *   - 'dynamic-extra'    : not a static and not SC (Country, Currency, etc.)
 *
 * @param {Array<{ value: string, label: string, category: string }>} statics
 * @param {Map<string, object>} liveByGrouping
 */
function mergeCatalog (statics, liveByGrouping) {
  const augmentedStatics = statics.map(s => {
    const live = liveByGrouping.get(s.value)
    return {
      ...s,
      additionalOptions: live?.additionalOptions || [],
      isAssetBased: live ? !!live.isAssetBased : true,
      source: live ? 'static-augmented' : 'static'
    }
  })

  const staticValues = new Set(statics.map(s => s.value))
  const dynamicExtras = []
  for (const [grouping, live] of liveByGrouping) {
    if (staticValues.has(grouping)) continue
    const isSC = grouping.startsWith(SC_PREFIX)
    dynamicExtras.push({
      value: grouping,
      label: live.name || grouping,
      category: live.category || (isSC ? SC_FALLBACK_CATEGORY : 'Other'),
      additionalOptions: live.additionalOptions || [],
      isAssetBased: !!live.isAssetBased,
      source: isSC ? 'dynamic-sc' : 'dynamic-extra'
    })
  }

  return [...augmentedStatics, ...dynamicExtras]
}

/**
 * Groups options by `category`, sorts each section alphabetically by label,
 * orders categories per PRIMARY_GROUPING_CATEGORIES then SECONDARY_GROUPING_
 * CATEGORIES, and tags each section with `tier`. Unknown firm categories
 * are appended alphabetically after curated secondaries (tier 'secondary').
 *
 * Pure -- exported for direct testing.
 *
 * @param {Array<{ value: string, label: string, category?: string }>} allOptions
 * @returns {Array<{ category: string, tier: 'primary' | 'secondary', options: Array }>}
 */
export function buildSections (allOptions) {
  const byCategory = new Map()
  for (const opt of allOptions) {
    const cat = opt.category || 'Other'
    if (!byCategory.has(cat)) byCategory.set(cat, [])
    byCategory.get(cat).push(opt)
  }
  for (const list of byCategory.values()) {
    list.sort((a, b) => a.label.localeCompare(b.label))
  }

  const primary = PRIMARY_GROUPING_CATEGORIES
    .filter(c => byCategory.has(c))
    .map(c => ({ category: c, tier: 'primary', options: byCategory.get(c) }))

  const secondaryCurated = SECONDARY_GROUPING_CATEGORIES
    .filter(c => byCategory.has(c))
    .map(c => ({ category: c, tier: 'secondary', options: byCategory.get(c) }))

  const secondaryExtras = [...byCategory.keys()]
    .filter(c => !KNOWN_CATEGORIES.has(c))
    .sort((a, b) => a.localeCompare(b))
    .map(c => ({ category: c, tier: 'secondary', options: byCategory.get(c) }))

  return [...primary, ...secondaryCurated, ...secondaryExtras]
}

/**
 * Catalog of available groupings for the Portfolio Analysis picker.
 *
 * Returns the picker-ready `sections` (grouped by category, tier-tagged)
 * plus `catalog` (the merged static + dynamic list with full metadata) and
 * a `getLabel` resolver used by the Selected blade, detail-panel title,
 * and toolbar breadcrumb.
 *
 * The dynamic data is sourced from Orion's
 * `GET /Reporting/Envision/GroupingOptions` (EnvisionController). That
 * endpoint returns the full per-client grouping catalog; we (1) augment
 * statics with endpoint-only metadata (`additionalOptions`, `isAssetBased`),
 * (2) add SC entries with the firm's display name, and (3) add any other
 * `isAssetBased: true` entries that aren't already statics, subject to the
 * exclusion lists in lib/constants.js.
 *
 * The Envision endpoint requires `Privileges.Reports` Read, which the
 * Summit API user already has (same privilege used by `/Reporting/Scope`).
 *
 * Multi-OC: React Query keys this query by clientId, so switching clients
 * refetches and the hook output flips to the new client's catalog.
 *
 * @param {number|null|undefined} clientId
 * @returns {{
 *   staticOptions: Array<object>,
 *   dynamicOptions: Array<object>,
 *   catalog: Array<object>,
 *   sections: Array<{ category: string, tier: 'primary' | 'secondary', options: Array<object> }>,
 *   isLoadingDynamic: boolean,
 *   getLabel: (wireValue: string) => string
 * }}
 *
 *  - `staticOptions`   -- `GROUPING_OPTIONS` as-is, reference-stable. Kept
 *    for callers that need just the curated list (and for tests).
 *  - `dynamicOptions`  -- SC-only subset of the merged catalog. Backward-
 *    compat field; new callers should prefer `catalog`.
 *  - `catalog`         -- merged list with `source` tag on each entry.
 *  - `sections`        -- picker-ready, with `tier` per section.
 *  - `isLoadingDynamic`-- true while the Orion call is in flight.
 *  - `getLabel(wire)`  -- resolves a wire value to its display label. SC
 *    and dynamic-extra entries return their firm label; statics and unknown
 *    wires return identity (statics have label === value).
 */
export function useGroupingCatalog (clientId) {
  // Use React Query's `isLoading` (= no data + fetching) rather than
  // `isFetching` (= any fetch, including background revalidation), so the
  // "Loading firm-specific classifications..." footer doesn't flash on
  // window-focus refetches when data is already cached.
  const { data: options, isLoading } = useOrionDomainQuery(
    'reporting/envision/grouping-options',
    clientId,
    {},
    { staleTime: 15 * 60 * 1000 }
  )

  return useMemo(() => {
    // The Orion-domain-query gateway wraps the Orion response as { data: [...] }
    // (see `executeOrionDomainQuery` server-side: `return { data }`). Unwrap
    // here, same convention as `transformResponse` for /Reporting/Scope.
    const list = Array.isArray(options?.data)
      ? options.data
      : (Array.isArray(options) ? options : [])

    const liveByGrouping = indexLive(list)
    const catalog = mergeCatalog(GROUPING_OPTIONS, liveByGrouping)
    const sections = buildSections(catalog)

    // Only non-static entries contribute to the label map -- statics have
    // label === value, so identity is the right fallback for them and for
    // any unknown wire.
    const labelMap = new Map()
    for (const opt of catalog) {
      if (opt.source !== 'static' && opt.source !== 'static-augmented') {
        labelMap.set(opt.value, opt.label)
      }
    }

    return {
      staticOptions: GROUPING_OPTIONS,
      dynamicOptions: catalog.filter(o => o.source === 'dynamic-sc'),
      catalog,
      sections,
      isLoadingDynamic: isLoading,
      getLabel: (wire) => labelMap.get(wire) || wire
    }
  }, [options, isLoading])
}
